import { Inject, Injectable, OnDestroy, Optional } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import {
	CommonState,
	MultiSessionLimitAction,
	MultiSessionLimitResponse,
	MultiSessionLimitSelector,
	MultiSessionLimitStrings,
} from "@obg/ngx.common";
import { merge, Observable, ReplaySubject, Subscription } from "rxjs";
import { distinctUntilChanged, tap } from "rxjs/operators";

import { OBG_VALIDATORS } from "../../validators/validators";
import { validateForm } from "../responsible-gaming-form-validators/validate-form";

import {
	MAX_TIME_LIMITS,
	MultiSessionLimitType,
	NOT_SET_TRANSLATION_KEY,
	SESSION_LIMIT_CONFIG,
	SessionLimitConfig,
} from "./session-limit.const";

@Injectable()
export class SessionLimitService implements OnDestroy {
	sessionLimitForm: UntypedFormGroup;

	hasCurrentLimits$ = new ReplaySubject<boolean>(1);
	currentLimits: MultiSessionLimitResponse | undefined;
	upcomingLimits: MultiSessionLimitResponse | undefined;
	validationKey: string | undefined;
	forceInvalidForm = false;

	weeklySuggestion: string | undefined;
	monthlySuggestion: string | undefined;
	placeholder = NOT_SET_TRANSLATION_KEY;

	private data$$: Subscription | undefined;

	constructor(
		@Optional() @Inject(SESSION_LIMIT_CONFIG) private sessionLimitConfig: SessionLimitConfig | null,
		private formBuilder: UntypedFormBuilder,
		private store: Store<CommonState>,
		private multiSessionLimitSelector: MultiSessionLimitSelector,
		private multiSessionLimitAction: MultiSessionLimitAction
	) {
		const commonValidator = [Validators.required, OBG_VALIDATORS.numericNotZero, this.forceInvalidator];
		const dailySessionLimit = new UntypedFormControl();
		const weeklySessionLimit = new UntypedFormControl();
		const monthlySessionLimit = new UntypedFormControl();
		dailySessionLimit.setValidators([
			...commonValidator,
			Validators.max(MAX_TIME_LIMITS.daily),
			OBG_VALIDATORS.numberSmallerOrEqual(() => weeklySessionLimit.value),
			OBG_VALIDATORS.numberSmallerOrEqual(() => monthlySessionLimit.value),
		]);

		weeklySessionLimit.setValidators([
			...commonValidator,
			Validators.max(MAX_TIME_LIMITS.weekly),
			OBG_VALIDATORS.numberBiggerOrEqual(() => dailySessionLimit.value),
			OBG_VALIDATORS.numberSmallerOrEqual(() => monthlySessionLimit.value),
		]);

		if (!sessionLimitConfig?.monthlySessionLimitNotRequired) {
			monthlySessionLimit.setValidators([
				...commonValidator,
				Validators.max(MAX_TIME_LIMITS.monthly),
				OBG_VALIDATORS.numberBiggerOrEqual(() => dailySessionLimit.value),
				OBG_VALIDATORS.numberBiggerOrEqual(() => weeklySessionLimit.value),
			]);
		}

		this.sessionLimitForm = this.formBuilder.group({ dailySessionLimit, weeklySessionLimit, monthlySessionLimit });
	}

	init() {
		let monthlySessionValidator$ = new Observable();

		if (this.sessionLimitConfig?.monthlySessionLimitNotRequired) {
			monthlySessionValidator$ = this.sessionLimitForm.controls.monthlySessionLimit.valueChanges.pipe(
				distinctUntilChanged(),
				tap((value) => {
					if (!value) {
						this.sessionLimitForm.controls.monthlySessionLimit.clearValidators();
						this.sessionLimitForm.controls.monthlySessionLimit.updateValueAndValidity();
						return;
					}
					this.sessionLimitForm.controls.monthlySessionLimit.setValidators([
						OBG_VALIDATORS.numericNotZero,
						OBG_VALIDATORS.numberBiggerOrEqual(
							() => this.sessionLimitForm.controls.dailySessionLimit.value
						),
						OBG_VALIDATORS.numberBiggerOrEqual(
							() => this.sessionLimitForm.controls.weeklySessionLimit.value
						),
						this.forceInvalidator,
						Validators.max(MAX_TIME_LIMITS.monthly),
					]);
					this.sessionLimitForm.controls.monthlySessionLimit.updateValueAndValidity();
				})
			);
		}

		this.store.dispatch(this.multiSessionLimitAction.get());

		const sessionLimits$ = this.store.pipe(select(this.multiSessionLimitSelector.get())).pipe(
			tap((sessionLimits) => {
				this.currentLimits = sessionLimits?.current;
				this.upcomingLimits = sessionLimits?.upcoming;
				this.hasCurrentLimits$.next(!!this.currentLimits);
			})
		);

		const formChanges$ = this.sessionLimitForm.valueChanges.pipe(
			tap((limits: MultiSessionLimitStrings) => {
				this.updatePlaceholders(limits);
				Object.keys(this.sessionLimitForm.controls).forEach((key) => {
					const control = this.sessionLimitForm.controls[key];
					control.updateValueAndValidity({ onlySelf: true });
				});
				this.sessionLimitForm.updateValueAndValidity({ emitEvent: false });
			})
		);

		this.data$$ = merge(monthlySessionValidator$, sessionLimits$, formChanges$).subscribe();
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	updateValue() {
		this.store.dispatch(this.multiSessionLimitAction.update(this.sessionLimitForm.value));
	}

	private updatePlaceholders(limits: MultiSessionLimitStrings) {
		const dailySessionLimit = Number(limits.dailySessionLimit);
		const weeklySessionLimit = Number(limits.weeklySessionLimit);
		const monthlySessionLimit = Number(limits.monthlySessionLimit);

		this.forceInvalidForm = false;
		if (this.currentLimits && !this.upcomingLimits) {
			const { validationKey, forceInvalidForm } = validateForm(
				[
					this.currentLimits.dailySessionLimit,
					this.currentLimits.weeklySessionLimit,
					this.currentLimits.monthlySessionLimit,
				],
				[dailySessionLimit, weeklySessionLimit, monthlySessionLimit],
				MultiSessionLimitType.Current
			);
			this.forceInvalidForm = forceInvalidForm;
			this.validationKey = validationKey;
		}

		if (this.upcomingLimits && this.upcomingLimits.dailySessionLimit !== 0) {
			const { validationKey, forceInvalidForm } = validateForm(
				[
					this.upcomingLimits.dailySessionLimit,
					this.upcomingLimits.weeklySessionLimit,
					this.upcomingLimits.monthlySessionLimit,
				],
				[dailySessionLimit, weeklySessionLimit, monthlySessionLimit],
				MultiSessionLimitType.Upcoming
			);
			this.forceInvalidForm = forceInvalidForm;
			this.validationKey = validationKey;
		}

		if (
			this.invalidValue(dailySessionLimit) &&
			this.invalidValue(weeklySessionLimit) &&
			this.invalidValue(monthlySessionLimit, !this.sessionLimitConfig?.monthlySessionLimitNotRequired)
		) {
			this.setDefaultPlaceholders();
			return;
		}

		if (dailySessionLimit && weeklySessionLimit && dailySessionLimit * 7 < weeklySessionLimit) {
			this.monthlySuggestion = Math.min(dailySessionLimit * 30, MAX_TIME_LIMITS.monthly).toString();
			return;
		}
		if (dailySessionLimit) {
			this.weeklySuggestion = Math.min(dailySessionLimit * 7, MAX_TIME_LIMITS.weekly).toString();
			this.monthlySuggestion = Math.min(dailySessionLimit * 30, MAX_TIME_LIMITS.monthly).toString();
			return;
		}

		this.setDefaultPlaceholders();
	}

	private invalidValue(value: number, notZero = true): boolean {
		return !Number.isInteger(value) || (notZero && value <= 0);
	}

	private setDefaultPlaceholders() {
		this.weeklySuggestion = undefined;
		this.monthlySuggestion = undefined;
	}

	private forceInvalidator = (_control: AbstractControl): { invalidate: false } | null =>
		!this.forceInvalidForm
			? null
			: {
					invalidate: false,
				};
}
